<template lang="pug">
v-container.pa-8(style="max-width:1080px")
  div.d-flex.justify-center.mt-16(v-if="isFetching")
    v-progress-circular(:width="2" :size="50" color="primary" indeterminate)

  template(v-else)
    v-row(no-gutters align="center")
      v-col.shrink
        v-btn.ml-n3(text icon @click="goToServices" height)
          v-icon(color="basic800" :size="24") mdi-arrow-left
      v-col.ml-3
        .text-2xl.font-semibold.neutral700--text {{ id ? $t('edit_service') : $t('create_new_service') }}

    v-form(ref="form")
      service-details
      v-divider.basic500.my-5
      service-request
      v-divider.basic500.my-5
      service-settings
      v-divider.basic500.my-5
      service-pre-questions
      v-divider.basic500.my-5
      service-pre-instructions
      v-divider.basic500.my-5
      service-post-instructions
      v-divider.basic500.my-5
      service-provider

    v-footer.white(app inset padless elevation="3")
      v-container.px-8.py-2
        div.d-flex.align-center
          v-spacer
          y-button(:label="id ? $t('save') : $t('finish')" color="neutral700" dark :loading="isLoading" @click="saveData")

</template>

<script>
import { mapGetters, mapActions } from 'vuex'
import ServiceDetails from '@/components/clinic/manage/ServiceDetails.vue'
import ServiceSettings from '@/components/clinic/manage/ServiceSettings.vue'
import ServiceRequest from '@/components/clinic/manage/ServiceRequest.vue'
import ServicePreQuestions from '@/components/clinic/manage/ServicePreQuestions.vue'
import ServicePreInstructions from '@/components/clinic/manage/ServicePreInstructions.vue'
import ServicePostInstructions from '@/components/clinic/manage/ServicePostInstructions.vue'
import ServiceProvider from '@/components/clinic/manage/ServiceProvider.vue'

export default {
  name: 'AppointmentService',
  components: { ServiceDetails, ServiceSettings, ServiceRequest, ServicePreQuestions, ServicePreInstructions, ServicePostInstructions, ServiceProvider },
  props: ['id'],
  data () {
    return {
      isFetching: false,
      isLoading: false,
      manageTab: 0,
      coverPhoto: null,
      photos: [],
    }
  },
  computed: {
    ...mapGetters({
      company: 'getCompany',
      form: 'clinic/getForm',
      tempService: 'clinic/getTempService'
    }),
  },
  mounted () {
    if (this.id) {
      this.fetchService()
    }
  },
  methods: {
    ...mapActions(['showMessage']),
    fetchService () {
      this.isFetching = true
      this.$axios.get(`/clinics/${this.company.slug}/services/${this.id}`)
        .then(resp => {
          this.$store.commit('clinic/setService', resp.data)
        })
        .catch(() => {
          this.showMessage(['error', this.$t('error_message.unable_to_complete_request')])
        })
        .finally(() => {
          this.isFetching = false
        })
    },
    saveData () {
      if (!this.$refs.form.validate()) {
        this.showMessage(['error', this.$t('error_message.please_fill_out_all_required')])
        return
      }
      this.isLoading = true
      let data = this.tempService
      if (this.tempService.pre_appointment || this.tempService.post_appointment) {
        data.instructions = {
          pre_appointment: this.tempService.pre_appointment,
          post_appointment: this.tempService.post_appointment
        }
        delete data.pre_appointment
        delete data.post_appointment
      }
      data.product.status = 'draft'
      if (this.id) {
        const endpoint = `/clinics/${this.company.slug}/services/${this.id}`
        this.$axios.patch(endpoint, data)
          .then(resp => {
            this.updateActivityLog2(endpoint, data, resp, 'PATCH', this.id)
            this.$store.commit('clinic/setService', resp.data)
            this.goToServices()
          })
          .catch(() => {
            this.showMessage(['error', this.$t('error_message.unable_to_complete_request')])
          })
          .finally(() => {
            this.isLoading = false
          })
      } else {
        if (data.photos) {
          this.coverPhoto = null
          this.photos = []
          data.photos.forEach((photo, index) => {
            if (index === 0) {
              this.coverPhoto = photo
            } else {
              this.photos.push(photo)
            }
          })
          delete data.photos
        }
        const endpoint = `/clinics/${this.company.slug}/services`
        this.$axios.post(endpoint, data)
          .then(resp => {
            this.updateActivityLog(endpoint, data, resp, 'POST')
            if (this.coverPhoto) {
              this.addCoverPhoto(resp.data.id)
            } else {
              this.isLoading = false
              this.goToServices()
            }
          })
          .catch(() => {
            this.showMessage(['error', this.$t('error_message.unable_to_complete_request')])
            this.isLoading = false
          })
      }
    },
    addCoverPhoto (serviceId) {
      const params = new FormData()
      params.append('product.image', this.coverPhoto.file)
      this.$axios.patch(`/clinics/${this.company.slug}/services/${serviceId}`, params)
        .then(() => {
          if (this.photos.length > 0) {
            this.addPhotos(serviceId)
          } else {
            this.isLoading = false
            this.goToServices()
          }
        })
        .catch(() => {
          this.isLoading = false
          this.showMessage(['error', this.$t('error_message.unable_to_upload_image')])
        })
    },
    async addPhotos (serviceId) {
      let hasError = false
      await this.asyncForEach(this.photos, async photo => {
        try {
          const params = new FormData()
          params.append('image', photo.file)
          await this.$axios.post(`/clinics/${this.company.slug}/services/${serviceId}/images`, params)
        } catch (error) {
          hasError = true
        }
      })
      this.isLoading = false
      if (hasError) {
        this.showMessage(['error', this.$t('error_message.unable_to_upload_image')])
        return
      }
      this.goToServices()
    },
    goToServices () {
      this.$router.push({ path: '/admin/clinic/manage', name: 'ClinicPageSetup', params: { fromTab: 3 } })
    },
    updateActivityLog (endpoint, payload, response, method, id) {
      let data = {
        company: this.company.id,
        object_id: id || null,
        object_ref: 'service',
        activity: 'Create new service',
        raw_data: { endpoint, payload, response: response.data, method}
      }
      this.$axios.post('/users/logs', data)
        .catch(() => {})
    },
    updateActivityLog2 (endpoint, payload, response, method, id) {
      let data = {
        company: this.company.id,
        object_id: id || null,
        object_ref: 'service',
        activity: 'Update service',
        raw_data: { endpoint, payload, response: response.data, method}
      }
      this.$axios.post('/users/logs', data)
        .catch(() => {})
    }
  },
  destroyed () {
    this.$store.commit('clinic/resetService')
    this.$store.commit('clinic/resetTempService')
  }
}
</script>

<style scoped>
.clickable {
  cursor: pointer;
}
.main-container {
  position: relative;
}
.separator {
  position: absolute;
  height:2px;
  top: 38px;
  left: 0px;
  right: 0px;
}
</style>